/** Version of the rules in this package, recorded in every file it writes. */
export const CONTRACT_VERSION = '2026.11';

export const PROFILES = ['P0', 'P1', 'P2', 'P3'] as const;
export type Profile = (typeof PROFILES)[number];

/** The `asdp-profile` custom property also holds this before Gate 1. */
export const UNCLASSIFIED = 'unclassified';
export type ProfileProperty = Profile | typeof UNCLASSIFIED;

/** ASDP-PRF-01: a derived profile is never below P1; P0 can only be chosen. */
export const MINIMUM_DERIVED_PROFILE: Profile = 'P1';

export const PROFILE_INFO: Readonly<
  Record<Profile, { name: string; meaning: string }>
> = {
  P0: {
    name: 'Experimental',
    meaning: 'Isolated experiment: synthetic data, no path to production',
  },
  P1: {
    name: 'Standard',
    meaning: 'Peer review, documented requirements and tests',
  },
  P2: {
    name: 'Controlled',
    meaning:
      'Formal traceability, security architecture review, independent verification',
  },
  P3: {
    name: 'Regulated / Critical',
    meaning:
      'Security architecture review reference, client autonomy authorisation, full separation of duties',
  },
};

export const profileRank = (profile: Profile) => PROFILES.indexOf(profile);

export const isProfile = (value: unknown): value is Profile =>
  typeof value === 'string' && (PROFILES as readonly string[]).includes(value);

export const higherProfile = (a: Profile, b: Profile): Profile =>
  profileRank(a) >= profileRank(b) ? a : b;

/** "P2 Controlled" */
export const profileLabel = (profile: Profile) =>
  `${profile} ${PROFILE_INFO[profile].name}`;

/** At P2 and above the stricter separation and evidence rules apply. */
export const isControlled = (profile: Profile) =>
  profileRank(profile) >= profileRank('P2');

/** Repository custom properties the gate App sets; org rulesets target them (ADR-0017). */
export const PROFILE_PROPERTY = 'asdp-profile';
export const STAGE_PROPERTY = 'asdp-stage';

export const STAGES = ['start', 'classified', 'build'] as const;
/** The `asdp-stage` custom property: which org ruleset applies (ADR-0017). */
export type Stage = (typeof STAGES)[number];

export const isStage = (value: unknown): value is Stage =>
  typeof value === 'string' && (STAGES as readonly string[]).includes(value);

export const STAGE_INFO: Readonly<
  Record<Stage, { name: string; merge: string; prod: string }>
> = {
  start: {
    name: 'Quick start',
    merge: 'Pull request, no approval needed, auto-merge',
    prod: "Doesn't exist",
  },
  classified: {
    name: 'Classified',
    merge: "One review by a teammate who isn't the author",
    prod: "Doesn't exist",
  },
  build: {
    name: 'Build',
    merge: 'Merge policy for the profile',
    prod: 'Exists; every deploy needs release approval (Gate 6)',
  },
};
